import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import DataForm from 'forms/DataForm';
import { request } from 'utilities/graph';
import { updateFacilitySettings, createFacilitySettings, updateClient } from 'graphql/mutations';
import { getClient } from 'graphql/queries';
import ImageGalleryUploader from 'components/ImageGalleryUploader';
import { useCache } from 'CacheProvider';
import moment from 'moment-timezone';
import cache from 'utilities/cache';
import uiSchema from './uiSchema';
import { getCustomizeDisadvantagedSettingsSchema } from 'forms/schemas';

export default function FacilitySettingsForm({ formData: inFormData, ...props }) {
  const { default: inSchema } = useMemo(() => require('./schema.js'), []);
  const [currentImageLogoS3Keys, setCurrentImageLogoS3Keys] = useState([]);
  const [currentImageCompanyStampS3Keys, setCurrentImageCompanyStampS3Keys] = useState([]);
  const [currentImageChairmanStampS3Keys, setCurrentImageChairmanStampS3Keys] = useState([]);
  const [currentImageAccountantStampS3Keys, setCurrentImageAccountantStampS3Keys] = useState([]);
  const [currentImageHandlerStampS3Keys, setCurrentImageHandlerStampS3Keys] = useState([]);
  const [newImageLogoS3Keys, setNewImageLogoS3Keys] = useState([]);
  const [newImageCompanyStampS3Keys, setNewImageCompanyStampS3Keys] = useState([]);
  const [newImageChairmanStampS3Keys, setNewImageChairmanStampS3Keys] = useState([]);
  const [newImageAccountantStampS3Keys, setNewImageAccountantStampS3Keys] = useState([]);
  const [newImageHandlerStampS3Keys, setNewImageHandlerStampS3Keys] = useState([]);

  // 團購相關狀態
  const [currentGroupPurchaseHomeImageS3Keys, setCurrentGroupPurchaseHomeImageS3Keys] = useState([]);
  const [newGroupPurchaseHomeImageS3Keys, setNewGroupPurchaseHomeImageS3Keys] = useState([]);
  const [currentFacilityAvatarS3Keys, setCurrentFacilityAvatarS3Keys] = useState([]);
  const [newFacilityAvatarS3Keys, setNewFacilityAvatarS3Keys] = useState([]);

  const { appGroup, setLogoS3Keys, selectedUserGroupParams, setSelectedUserGroupParams } = useCache();
  const [formData, setFormData] = useState();
  const [enableOnlinePayment, setEnableOnlinePayment] = useState(false);
  const [isEligibleForQRCodePod, setIsEligibleForQRCodePod] = useState(false);
  const schema = JSON.parse(JSON.stringify(inSchema));
  const [isEligibleForReceiptPrinter, setIsEligibleForReceiptPrinter] = useState(false);
  const facilityMode = appGroup === 'FacilityAdmins';

  useEffect(() => {
    (async () => {
      let clientId;
      if (appGroup === 'Admins') {
        clientId = inFormData.id;
      } else {
        clientId = cache.get('app:facilityId');
      }
      const { data: { getClient: client } } = await request(getClient, { id: clientId });
      if (client?.featureConfiguration?.isEligibleForOnlinePayment) {
        if (client?.featureConfiguration?.isEligibleForGroupPurchase || client?.featureConfiguration?.isEligibleForIndividualPurchase) {
          setEnableOnlinePayment(true);
        }
      }

      if (!inFormData) {
        return;
      }
      if (inFormData.facilitySettings?.logoS3Keys?.[0]) {
        setCurrentImageLogoS3Keys(inFormData.facilitySettings.logoS3Keys);
        setNewImageLogoS3Keys(inFormData.facilitySettings.logoS3Keys);
      }
      if (inFormData.facilitySettings?.companyStampS3Keys?.[0]) {
        setCurrentImageCompanyStampS3Keys(inFormData.facilitySettings.companyStampS3Keys);
        setNewImageCompanyStampS3Keys(inFormData.facilitySettings.companyStampS3Keys);
      }
      if (inFormData.facilitySettings?.chairmanStampS3Keys?.[0]) {
        setCurrentImageChairmanStampS3Keys(inFormData.facilitySettings.chairmanStampS3Keys);
        setNewImageChairmanStampS3Keys(inFormData.facilitySettings.chairmanStampS3Keys);
      }
      if (inFormData.facilitySettings?.accountantStampS3Keys?.[0]) {
        setCurrentImageAccountantStampS3Keys(inFormData.facilitySettings.accountantStampS3Keys);
        setNewImageAccountantStampS3Keys(inFormData.facilitySettings.accountantStampS3Keys);
      }
      if (inFormData.facilitySettings?.handlerStampS3Keys?.[0]) {
        setCurrentImageHandlerStampS3Keys(inFormData.facilitySettings.handlerStampS3Keys);
        setNewImageHandlerStampS3Keys(inFormData.facilitySettings.handlerStampS3Keys);
      }

      // 團購相關圖片初始化
      if (inFormData.facilitySettings?.groupPurchaseHomeImageS3Keys?.[0]) {
        setCurrentGroupPurchaseHomeImageS3Keys(inFormData.facilitySettings.groupPurchaseHomeImageS3Keys);
        setNewGroupPurchaseHomeImageS3Keys(inFormData.facilitySettings.groupPurchaseHomeImageS3Keys);
      }
      if (inFormData.facilitySettings?.facilityAvatarS3Keys?.[0]) {
        setCurrentFacilityAvatarS3Keys(inFormData.facilitySettings.facilityAvatarS3Keys);
        setNewFacilityAvatarS3Keys(inFormData.facilitySettings.facilityAvatarS3Keys);
      }

      if (inFormData.featureConfiguration?.isEligibleForQRCodePod) {
        setIsEligibleForQRCodePod(inFormData.featureConfiguration.isEligibleForQRCodePod);
      }
      if (inFormData.featureConfiguration?.isEligibleForReceiptPrinter) {
        setIsEligibleForReceiptPrinter(inFormData.featureConfiguration.isEligibleForReceiptPrinter);
      }
      if (inFormData.facilitySettings) {
        setFormData(inFormData);
      } else {
        setFormData({
          ...inFormData,
          facilitySettings: { // 避免rjsf跳出格式檢查錯誤
            lunchStartTime: null,
            lunchEndTime: null,
            dinnerStartTime: null,
            dinnerEndTime: null,
            deliverySetting: null,
            receiptInfo: null,
          },
        });
      }
    })();
  }, [inFormData]);

  const updateFunc = async (data) => {
    data.facilitySettings.logoS3Keys = newImageLogoS3Keys;
    data.facilitySettings.companyStampS3Keys = newImageCompanyStampS3Keys;
    data.facilitySettings.chairmanStampS3Keys = newImageChairmanStampS3Keys;
    data.facilitySettings.accountantStampS3Keys = newImageAccountantStampS3Keys;
    data.facilitySettings.handlerStampS3Keys = newImageHandlerStampS3Keys;

    // 團購相關欄位
    data.facilitySettings.groupPurchaseHomeImageS3Keys = newGroupPurchaseHomeImageS3Keys;
    data.facilitySettings.facilityAvatarS3Keys = newFacilityAvatarS3Keys;

    if (facilityMode) {
      setLogoS3Keys([...newImageLogoS3Keys]);
    }
    if (data.facilitySettings.id) {
      await request(updateFacilitySettings, { input: data.facilitySettings });
    } else {
      const now = moment().toISOString();
      const userName = cache.get('app:userName');
      Object.assign(data.facilitySettings, {
        createdAt: now,
        createdBy: userName,
        updatedAt: now,
        updatedBy: userName,
      });
      const { data: { createFacilitySettings: { id: facilitySettingsId } } } =
        await request(createFacilitySettings, { input: data.facilitySettings });
      data.facilitySettings.id = facilitySettingsId;
      await request(updateClient, { input: {
        id: data.id,
        type: data.type,
        isActive: data.isActive,
        county: data.county,
        name: data.name,
        address: data.address,
        email: data.email,
        usersLimit: data.usersLimit,
        eldersLimit: data.eldersLimit,
        createdAt: data.createdAt,
        createdBy: data.createdBy,
        updatedAt: now,
        updatedBy: userName,
        facilitySettingsId: facilitySettingsId,
      } });
    }
    if (facilityMode) {
      selectedUserGroupParams.clientData.facilitySettings = data.facilitySettings;
      setSelectedUserGroupParams(selectedUserGroupParams);
      cache.set('app:selectedUserGroupParams', selectedUserGroupParams);
    }
  };

  if (!formData) {
    return <></>;
  }

  if (!enableOnlinePayment) {
    delete schema.properties.facilitySettings.properties.onlinePaymentSettings;
  }
  if (!isEligibleForQRCodePod) {
    delete schema.properties.facilitySettings.properties.qrCodeSettings;
  }
  if (!isEligibleForReceiptPrinter) {
    delete schema.properties.facilitySettings.properties.receiptPrinterSettings;
  }

  return (
    <DataForm
      schema={schema}
      uiSchema={uiSchema}
      createFunc={() => {}}
      updateFunc={updateFunc}
      formData={formData}
      dirty={true}
      extMappings={[{
        key: 'disadvantagedType',
        func: () => {
          let clientId;
          if (appGroup === 'Admins') {
            clientId = inFormData.id;
          } else {
            clientId = cache.get('app:facilityId');
          }
          return getCustomizeDisadvantagedSettingsSchema(clientId, false);
        },
      }]}
      formContext={{ documentTitleImageS3KeysPrefix: `facilitySettings/${inFormData.id}/QRCodeDocumentTitleImage` }}
      {...props}
    >
      <Typography variant="h5">
        商標圖示
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在左側功能清單上方
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/logo`}
        s3Keys={currentImageLogoS3Keys}
        onUpdateS3Keys={setNewImageLogoS3Keys}
      />
      <Typography variant="h5" style={{ marginTop: 10 }}>
        收據公司章
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在收據，建議尺寸 150 x 150
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/companyStamp`}
        s3Keys={currentImageCompanyStampS3Keys}
        onUpdateS3Keys={setNewImageCompanyStampS3Keys}
      />
      <Typography variant="h5" style={{ marginTop: 10 }}>
        收據主管章
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在收據，建議尺寸 50 x 50
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/chairmanStamp`}
        s3Keys={currentImageChairmanStampS3Keys}
        onUpdateS3Keys={setNewImageChairmanStampS3Keys}
      />
      <Typography variant="h5" style={{ marginTop: 10 }}>
        收據會計章
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在收據，建議尺寸 50 x 50
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/accountantStamp`}
        s3Keys={currentImageAccountantStampS3Keys}
        onUpdateS3Keys={setNewImageAccountantStampS3Keys}
      />
      <Typography variant="h5" style={{ marginTop: 10 }}>
        收據經手人章
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在收據，建議尺寸 50 x 50
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/handlerStamp`}
        s3Keys={currentImageHandlerStampS3Keys}
        onUpdateS3Keys={setNewImageHandlerStampS3Keys}
      />

      <Typography variant="h5" style={{ marginTop: 10 }}>
        團購設定
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        設定團購頁面的相關資訊
      </Typography>
      <Divider />

      <Typography variant="h6" style={{ marginTop: 10 }}>
        團購頁面首頁圖片
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在團購頁面首頁，建議尺寸 700 x 480
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/groupPurchaseHomeImage`}
        s3Keys={currentGroupPurchaseHomeImageS3Keys}
        onUpdateS3Keys={setNewGroupPurchaseHomeImageS3Keys}
        maxFileSize={2 * 1024 * 1024} // 2MB
        cropSettings={{ width: 722, height: 484 }}
        showFileSizeLimit={true}
      />

      <Typography variant="h6" style={{ marginTop: 10 }}>
        機構頭像
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在團購頁面，建議尺寸 500 x 500
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/facilityAvatar`}
        s3Keys={currentFacilityAvatarS3Keys}
        onUpdateS3Keys={setNewFacilityAvatarS3Keys}
        maxFileSize={2 * 1024 * 1024} // 2MB
        showFileSizeLimit={true}
      />

      <Typography variant="h6" style={{ marginTop: 10 }}>
        團購規則
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        設定團購相關規則和說明
      </Typography>
      <Divider />
      <div style={{ marginTop: 10, marginBottom: 10 }}>
        <textarea
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontFamily: 'inherit',
            fontSize: '14px',
            resize: 'vertical',
          }}
          placeholder="請輸入團購規則和說明..."
          value={formData?.facilitySettings?.clientGroupPurchaseRule || ''}
          onChange={(e) => {
            const newFormData = { ...formData };
            if (!newFormData.facilitySettings) {
              newFormData.facilitySettings = {};
            }
            newFormData.facilitySettings.clientGroupPurchaseRule = e.target.value;
            setFormData(newFormData);
          }}
        />
      </div>
    </DataForm>
  );
}

FacilitySettingsForm.propTypes = {
  data: PropTypes.object,
  formData: PropTypes.object,
  onComplete: PropTypes.func,
};
